import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  signInWithRedirect,
  getRedirectResult,
  sendEmailVerification,
  sendPasswordResetEmail,
  User,
  UserCredential,
  updateProfile,
  signInWithPopup
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  setDoc
} from 'firebase/firestore';
import { app } from '../firebase';
import { logger } from '../utils/logger';
import { getUserProfileByUid, ensureUserRole } from '../services/userService';
import { UserProfile } from '../types/user';

/**
 * Type definitions for the auth context
 */
interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  profileLoading: boolean;
  hasUsername: boolean;
  signIn: (email: string, password: string) => Promise<UserCredential>;
  signUp: (email: string, password: string, firstName: string, lastName?: string, gender?: string, genderOther?: string, birthDate?: string) => Promise<UserCredential>;
  //Random comment
  signOut: () => Promise<void>;
  signInWithGoogleRedirect: () => Promise<void>;
  signInWithGooglePopup: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  verifyEmail: () => Promise<void>;
  isEmailVerified: boolean;
  updateUserProfile: (profileData: { displayName?: string | null; photoURL?: string | null }) => Promise<void>;
  updateExtendedProfile: (profileData: Partial<UserProfile>) => Promise<void>;
  checkUsernameExists: (username: string) => Promise<boolean>;
  setUserUsername: (username: string) => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  watchEmailVerificationStatus: (onVerificationChange: (verified: boolean) => void) => () => void;
}

// Create the auth context with a default value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Initialize Firebase Auth
const auth = getAuth(app);

/**
 * Provider component that wraps your app and makes auth object available
 * to any child component that calls useAuth().
 */
export function AuthProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [profileLoading, setProfileLoading] = useState<boolean>(false);
  
  // Derived state - does the user have a username set?
  const hasUsername = Boolean(userProfile?.username);

  // This will be undefined when AuthProvider is mounted before AppProvider
  // That's expected - we'll handle it in the effect

  // Check if user's email is verified
  const isEmailVerified = currentUser?.emailVerified || false;

  // Sign in with email and password
  async function signIn(email: string, password: string): Promise<UserCredential> {
    logger.info('🔐 Attempting to sign in with email and password');
    const result = await signInWithEmailAndPassword(auth, email, password);
    
    // After signing in, refresh the user profile
    await refreshUserProfile();
    
    return result;
  }
  // Sign up with email and password
  async function signUp(email: string, password: string, firstName: string, lastName?: string, gender?: string, genderOther?: string, birthDate?: string): Promise<UserCredential> {
    logger.info('📝 Attempting to create a new user with email and password');
    const result = await createUserWithEmailAndPassword(auth, email, password);
    
    // Send email verification after successful sign-up
    if (result.user) {
      // Store additional profile data
      if (firstName || gender || birthDate) {
        const userRef = doc(getFirestore(app), 'users', result.user.uid);
        const timestamp = Date.now();
        
        try {
          const profileData: any = {
            uid: result.user.uid,
            email: email,
            role: 'user',
            membershipStatus: 'active',
            createdAt: timestamp,
            updatedAt: timestamp
          };
          
          // Store private information (real names)
          if (firstName) profileData.firstName = firstName;
          if (lastName) profileData.lastName = lastName;
          
          if (birthDate) profileData.birthDate = birthDate;
          if (gender) {
            profileData.gender = gender;
            if (gender === 'Other' && genderOther) {
              profileData.genderOther = genderOther;
            }
          }
          
          await setDoc(userRef, profileData);
          logger.info('✅ User profile data saved');
        } catch (error) {
          logger.error('❌ Error saving profile data', error);
          // Don't throw, we still want the account creation to succeed
        }
      }
        try {
        // Always send verification email
        await sendEmailVerification(result.user);
        
        // Store timestamp in localStorage for cooldown enforcement
        localStorage.setItem('verificationEmailSent', Date.now().toString());
        
        logger.info('📧 Verification email sent automatically upon account creation');
      } catch (error) {
        logger.error('❌ Error sending verification email during signup', error);
        // Don't throw, we still want the account creation to succeed
      }
    }
    
    return result;
  }

  // Sign out
  async function signOut(): Promise<void> {
    logger.info('🚪 Signing out');
    
    try {
      // Set a flag to indicate intentional logout
      sessionStorage.setItem('userLoggedOut', 'true');
      
      // Clear any session storage or local storage authentication items if used
      localStorage.removeItem('firebaseAuthRedirect');
      sessionStorage.removeItem('firebaseAuthRedirect');
      
      // Force reload after sign out to clear any in-memory state
      const signOutPromise = firebaseSignOut(auth);
      
      await signOutPromise;
      
      // Ensure currentUser is set to null immediately
      setCurrentUser(null);
      
      logger.info('✅ User signed out successfully');
      
      // Optional: Redirect to home page or login page
      window.location.href = '/';
      
      return signOutPromise;
    } catch (error) {
      logger.error('❌ Error during sign out', error);
      throw error;
    }
  }

  // Sign in with Google
  async function signInWithGoogleRedirect(): Promise<void> {
    logger.info('🔐 Initiating Google sign-in redirect');
    const provider = new GoogleAuthProvider();
    await signInWithRedirect(auth, provider);
  }
  // Sign in with Google (Popup)
  async function signInWithGooglePopup(): Promise<void> {
    logger.info('🔐 Initiating Google sign-in popup');
    const provider = new GoogleAuthProvider();
    try {
      setLoading(true);
      const result = await signInWithPopup(auth, provider);
      logger.info('✅ Successfully signed in with Google (popup)', { email: result.user.email });
      
      // Store Google user information privately (names from Google account)
      if (result.user) {
        const userRef = doc(getFirestore(app), 'users', result.user.uid);
        const timestamp = Date.now();
        
        try {
          // Parse displayName to extract first and last names
          const displayName = result.user.displayName || '';
          const nameParts = displayName.split(' ');
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';
          
          const profileData: any = {
            uid: result.user.uid,
            email: result.user.email || '',
            role: 'user',
            membershipStatus: 'active',
            createdAt: timestamp,
            updatedAt: timestamp,
            // Store real names privately
            firstName: firstName,
            lastName: lastName,
          };
          
          await setDoc(userRef, profileData, { merge: true });
          logger.info('✅ Google user profile data saved privately');
        } catch (error) {
          logger.error('❌ Error saving Google profile data', error);
        }
      }
      
      // Fetch user profile to check if they have a username
      await refreshUserProfile();
      const profile = await getUserProfileByUid(result.user.uid);
      
      if (!profile || !profile.username) {
        // No username, redirect to username selection
        window.location.href = '/choose-username';
      } else {
        // Username exists, redirect to profile
        window.location.href = '/my-profile';
      }
    } catch (error: any) {
      logger.error('❌ Error with Google sign-in popup', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }

  // Send password reset email
  function resetPassword(email: string): Promise<void> {
    logger.info('🔑 Sending password reset email');
    return sendPasswordResetEmail(auth, email);
  }

  // Send email verification
  function verifyEmail(): Promise<void> {
    logger.info('📧 Sending email verification');
    if (currentUser) {
      return sendEmailVerification(currentUser);
    }
    return Promise.reject(new Error('No user logged in'));
  }
  
  // Update user profile
  async function updateUserProfile(profileData: { displayName?: string | null; photoURL?: string | null }): Promise<void> {
    if (!currentUser) {
      logger.error('❌ Cannot update profile: No user logged in');
      return Promise.reject(new Error('No user logged in'));
    }
    
    try {
      logger.info('👤 Updating user profile', { ...profileData });
      await updateProfile(currentUser, profileData);
      
      // Force a refresh of the current user object to ensure all components using it get updated
      setCurrentUser({ ...currentUser });
      logger.info('✅ Profile updated successfully');
    } catch (error: any) {
      logger.error('❌ Error updating profile', error);
      return Promise.reject(error);
    }
  }

  // Fetch user profile from Firestore
  const refreshUserProfile = useCallback(async (): Promise<void> => {
    if (!currentUser) {
      setUserProfile(null);
      return;
    }
    
    try {
      setProfileLoading(true);
      logger.info('🔄 Fetching user profile from Firestore', { uid: currentUser.uid });
      
      // Ensure the user has a role set in Firestore
      await ensureUserRole(currentUser.uid);
      
      const profile = await getUserProfileByUid(currentUser.uid);
      setUserProfile(profile);
      
      logger.info('✅ User profile fetched', { hasProfile: !!profile });
    } catch (error) {
      logger.error('❌ Error fetching user profile', error);
      // Don't throw here - we don't want this to crash the app
    } finally {
      setProfileLoading(false);
    }
  }, [currentUser]);
  
  // Update user's extended profile in Firestore
  const updateExtendedProfile = async (profileData: Partial<UserProfile>): Promise<void> => {
    if (!currentUser) {
      logger.error('❌ Cannot update extended profile: No user logged in');
      throw new Error('No user logged in');
    }
    
    try {
      const { updateUserProfile } = await import('../services/userService');
      await updateUserProfile(currentUser, profileData);
      
      // Refresh the profile to get updated data
      await refreshUserProfile();
      
      logger.info('✅ Extended profile updated successfully');
    } catch (error) {
      logger.error('❌ Error updating extended profile', error);
      throw error;
    }
  };
  
  // Check if a username exists
  const checkUsernameExists = async (username: string): Promise<boolean> => {
    try {
      const { validateUsername } = await import('../services/userService');
      const result = await validateUsername(username);
      return !result.isValid;
    } catch (error) {
      logger.error('❌ Error checking username', error);
      throw error;
    }
  };
  
  // Set user's username
  const setUserUsername = async (username: string): Promise<void> => {
    if (!currentUser) {
      logger.error('❌ Cannot set username: No user logged in');
      throw new Error('No user logged in');
    }
    
    try {
      const { setUsername } = await import('../services/userService');
      await setUsername(currentUser, username);
      
      // Refresh the profile to get updated data with username
      await refreshUserProfile();
      
      logger.info('✅ Username set successfully', { username });
    } catch (error) {
      logger.error('❌ Error setting username', error);
      throw error;
    }
  };
  
  // Watch the email verification status of the current user
  // This function will periodically check if the user's email has been verified
  const watchEmailVerificationStatus = (onVerificationChange: (verified: boolean) => void): (() => void) => {
    if (!currentUser) {
      return () => {}; // No cleanup needed if no user
    }
    
    logger.info('🔄 Starting to watch email verification status');
    
    // Set up an interval to check user verification status
    const intervalId = setInterval(async () => {
      try {
        // Force refresh the user to get the latest verification status
        await currentUser.reload();
        const freshUser = auth.currentUser;
        
        if (freshUser && freshUser.emailVerified) {
          logger.info('✅ Email has been verified during watch');
          onVerificationChange(true);
          clearInterval(intervalId); // Stop checking once verified
        }
      } catch (error) {
        logger.error('❌ Error checking verification status during watch', error);
      }
    }, 5000); // Check every 5 seconds
    
    // Return cleanup function
    return () => {
      logger.info('🛑 Stopped watching email verification status');
      clearInterval(intervalId);
    };
  };
  
  // Effect to handle redirect result from Google sign-in
  useEffect(() => {
    // Add a flag to prevent redirect result processing when user has explicitly logged out
    const isLogoutIntentional = sessionStorage.getItem('userLoggedOut') === 'true';
    
    const handleRedirectResult = async () => {
      // Skip redirect processing if logout was intentional
      if (isLogoutIntentional) {
        logger.info('🔄 Skipping Google redirect check due to explicit logout');
        sessionStorage.removeItem('userLoggedOut');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        logger.info('🔄 Checking for Google sign-in redirect result');
        const result = await getRedirectResult(auth);
        
        if (result) {
          // Google Sign In was successful
          const user = result.user;
          logger.info('🔐 Successfully signed in with Google', { email: user.email });
          
          // Fetch user profile to check if they have a username
          await refreshUserProfile();
          const profile = await getUserProfileByUid(user.uid);
          
          if (!profile || !profile.username) {
            // No username, redirect to username selection
            window.location.href = '/choose-username';
            logger.info('👤 Redirecting to username selection after Google sign-in');
          } else {
            // Username exists, redirect to profile
            window.location.href = '/my-profile';
            logger.info('👤 Redirecting to profile page after Google sign-in');
          }
        }
      } catch (error: any) {
        logger.error('❌ Error with Google sign-in redirect', error);
        // Could trigger a global error state or notification here
      } finally {
        setLoading(false);
      }
    };

    // Process redirect result when component mounts
    handleRedirectResult();
  }, [refreshUserProfile]);  
  
  // Subscribe to user on auth state change  
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      logger.debug('🔄 onAuthStateChanged triggered. User object:', user);
      
      // Always keep loading true until we've fully resolved the auth state
      // Don't set loading to false until we've also loaded the profile if there is a user
      
      // Check for intentional logout
      const isLogoutIntentional = sessionStorage.getItem('userLoggedOut') === 'true';
      
      if (isLogoutIntentional && user) {
        // If user is still present but logout was intentional, force signout
        logger.info('🔒 Intentional logout detected but user still present, forcing signout');
        await firebaseSignOut(auth);
        logger.debug('🔄 setCurrentUser called with null due to forced signout');
        setCurrentUser(null);
        setUserProfile(null);
        setLoading(false); // Done loading after logout
      } else {
        // Normal auth state handling
        logger.debug('🔄 setCurrentUser called with user:', user);
        setCurrentUser(user);
        
        if (user) {
          logger.info(`🔓 User authenticated: ${user.email}`);
          
          // Set loading to false only after we've loaded the profile
          // This ensures that hasUsername will be accurate before components render
          try {
            // Fetch user profile from Firestore (leave loading true while fetching)
            setProfileLoading(true);
            await refreshUserProfile();
          } finally {
            // Always set loading to false when done, even if there was an error
            setProfileLoading(false);
            setLoading(false);
          }
        } else {
          // No user, we can set loading to false immediately
          logger.info('🔒 No user authenticated');
          setUserProfile(null);
          setLoading(false);
        }
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [refreshUserProfile]);
  // Create the auth value object
  const value: AuthContextType = {
    currentUser,
    userProfile,
    loading,
    profileLoading,
    hasUsername,
    signIn,
    signUp,
    signOut,
    signInWithGoogleRedirect,
    signInWithGooglePopup,
    resetPassword,
    verifyEmail,
    isEmailVerified,
    updateUserProfile,
    updateExtendedProfile,
    checkUsernameExists,
    setUserUsername,
    refreshUserProfile,
    watchEmailVerificationStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

/**
 * Hook for using the auth context
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
